/// <reference path="../ihttperrorhandler.ts" />
/// <reference path="../errordetails.ts" />
/// <reference path="../../../../typings/index.d.ts" />
/// <reference path="../../apimodel.d.ts" />
/// <reference path="../errordetail.ts" />
/// <reference path="app.common.services.module.ts" />
/// <reference path="../ilodashmixins.d.ts" />
/// <reference path="modelstatevalidationservice.ts" />
/// <reference path="apiservice.ts" />
/// <reference path="authenticationservice.ts" />

module App.Common.Services {
	'use strict';

	export class ErrorHandlerService {

		public static $inject = [
			'$uibModal',
			'$rootScope',
			'$http',
			'$q',
			'$state',
			'$injector',
			'app.common.services.modelStateValidationService',
			'app.common.services.globalSettingsService',
			'app.common.services.navigationHelperService'
		];

		constructor(
			private $uibModal: angular.ui.bootstrap.IModalService,
			private $rootScope: angular.IRootScopeService,
			private $http: angular.IHttpService,
			private $q: angular.IQService,
			private $state: angular.ui.IStateService,
			private $injector: angular.auto.IInjectorService,
			private modelStateValidation: Services.ModelStateValidationService,
			private globalSettings: Services.GlobalSettingsService,
			private navigationHelper: Services.NavigationHelperService) {
		}

		public errorWindow: angular.ui.bootstrap.IModalServiceInstance;

		public errorShown = false;

		private lastErrorMessage: string;

		public handleException(error: any): void {

			if (!error) { return; }
			if (typeof error === 'string') { error = new Error(error); }

			//Prevent exception loops
			if (this.errorShown || this.lastErrorMessage === error.message) { return; }
			this.lastErrorMessage = error.message;

			//Log the client-side exception to the server (fire-and-forget, wrapped in a try/catch to 
			//prevent errors in the logging process blowing up the display of the original error)
			try {
				this.$http({
					url: 'api/ClientExceptions',
					method: 'POST',
					data: {
						message: error.message,
						stackTrace: error.stack
					}
				});
			} catch (e) { }

			this.displayError(this.getErrorDetails(error));
		}

		public handleHttpError(result: angular.IHttpPromiseCallbackArg<any>, suppressDisplay = false): angular.IPromise<angular.IHttpPromiseCallbackArg<any>> {

			//Validation errors (model state) 
			if (result.status === 400 && result.data && result.data.modelState) {
				if (this.modelStateValidation.handleModelState(result.data.modelState)) {
					return this.$q.reject(result);
				}
			}

			var errorDetails = this.getErrorDetails(result);

			//DNS failure, server offline, CORS access error etc.
			if (result.status === -1) {
				errorDetails.title = 'Network error';
				errorDetails.userMessage = 'Sorry, an error has occurred whilst attempting to communicate with the server';
			}

			//We intentionally create this scenario when the database is offline and show a meaningful reason to the user
			else if (result.status === 503) {
				errorDetails.title = 'Service unavailable';
				errorDetails.userMessage = (result && result.data)
					? result.data
					: 'Sorry, the requested service is currently unavailable';
			}

			if (!suppressDisplay) {
				this.displayError(errorDetails);
			}

			return this.$q.reject(result);
		}

		public displayError(errorDetails: ErrorDetails) {

			if (this.errorShown) { return; }
			this.errorShown = true;

			errorDetails.showTechnicalDetails = this.globalSettings.showDebugInfo;

			var config = {} as angular.ui.bootstrap.IModalSettings;
			config.animation = true;
			config.backdrop = 'static';
			config.keyboard = false;
			config.size = 'lg';
			config.controller = 'app.common.controllers.errorController';
			config.controllerAs = 'vm';
			config.bindToController = true;
			config.templateUrl = 'Templates/Common/ErrorView.html';
			config.resolve = {
				model: errorDetails
			};

			this.errorWindow = this.$uibModal.open(config);
		}

		//Normalize disparate error object types into a standardised structure
		public getErrorDetails(error: any): ErrorDetails {

			var errorDetails = new ErrorDetails();

			if (!error) { return errorDetails; }

			if (typeof error === 'string') { error = new Error(error); }

			if (error.title) {
				errorDetails.title = error.title;
			}
			if (error.userMessage) {
				errorDetails.userMessage = error.userMessage;
			}
			if (error.message || error.exceptionMessage) {
				errorDetails.errorDetails.push(new ErrorDetail('Message', error.message || error.exceptionMessage));
			}
			if (error.exceptionType) {
				errorDetails.errorDetails.push(new ErrorDetail('Exception Type', error.exceptionType));
			}
			if (error.stack || error.stackTrace) {
				errorDetails.errorDetails.push(new ErrorDetail('Stack Trace', _.htmlWhitespace(error.stack || error.stackTrace), true));
			}
			if (error.status) {
				errorDetails.errorDetails.push(new ErrorDetail('HTTP Response Code', error.status));
			}
			if (error.statusText) {
				errorDetails.errorDetails.push(new ErrorDetail('HTTP Response Message', error.statusText));
			}
			if (error.data) {
				if (error.data.message) {
					errorDetails.errorDetails.push(new ErrorDetail('Message', error.data.message));
				}
				if (error.data.messageDetail) {
					errorDetails.errorDetails.push(new ErrorDetail('Message Detail', error.data.messageDetail));
				}
				if (error.data.error_description) {
					errorDetails.errorDetails.push(new ErrorDetail('Error Description', error.data.error_description));
				}
				if (error.data.exceptionMessage) {
					errorDetails.errorDetails.push(new ErrorDetail('Exception Message', error.data.exceptionMessage));
				}
				if (error.data.exceptionType) {
					errorDetails.errorDetails.push(new ErrorDetail('Exception Type', error.data.exceptionType));
				}
				if (error.data.stackTrace) {
					errorDetails.errorDetails.push(new ErrorDetail('Stack Trace', _.htmlWhitespace(error.data.stackTrace), true));
				}

				let inner = error.data.innerException;
				while (inner && inner.innerException) {
					inner = inner.innerException;
				}

				if (inner) {
					if (inner.message) {
						errorDetails.errorDetails.push(new ErrorDetail('Innermost Message', inner.message));
					}
					if (inner.exceptionMessage) {
						errorDetails.errorDetails.push(new ErrorDetail('Innermost Exception Message', inner.exceptionMessage));
					}
					if (inner.exceptionType) {
						errorDetails.errorDetails.push(new ErrorDetail('Innermost Exception Type', inner.exceptionType));
					}
					if (inner.stackTrace) {
						errorDetails.errorDetails.push(new ErrorDetail('Innermost Stack Trace', _.htmlWhitespace(inner.stackTrace), true));
					}
				}
			}

			if (error.config) {
				if (error.config.url) {
					errorDetails.errorDetails.push(new ErrorDetail('Request URL', error.config.url));
				}
				if (error.config.method) {
					errorDetails.errorDetails.push(new ErrorDetail('Request Method', error.config.method));
				}
				if (error.config.params) {
					errorDetails.errorDetails.push(new ErrorDetail('Request Parameters', error.config.params));
				}
			}

			return errorDetails;

		}

	}

	angular
		.module('app.common.services')
		.service('app.common.services.errorHandlerService', ErrorHandlerService);
}